import { Add, Delete } from '@mui/icons-material';
import { Box, Button, Chip, IconButton, Link, Stack, Typography } from '@mui/material';
// Embedded, client-side, inline-editable grid — not a server-backed list. ServerDataGrid wraps
// server pagination/sort and doesn't expose the editing surface we need (processRowUpdate, edit
// cell renderers).
// eslint-disable-next-line no-restricted-imports
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import {
  fetchCustomerCatalog,
  fetchCustomerCatalogsByProduct,
  patchCustomerCatalog,
  postCustomerCatalog,
} from '@sophocles/api/customerCatalog';
import { CellAutocomplete } from '@sophocles/data-grids/components/cells';
import { fetchCustomerListWithFilters } from '@sophocles/entities/Customer';
import { productSlice } from '@sophocles/features/warehouse/products/slice';

const NEW_ROW_PREFIX = '__new__';
const GHOST_ID = '__ghost_add__';

/**
 * Customer ↔ Product price-override matrix, rendered as an inline-editable
 * DataGridPro.
 *
 * Two modes (mutually exclusive):
 *   - customerId  – list of products this customer has overrides for.
 *   - product     – list of customers with overrides for this product.
 *
 * Behavior:
 *   - Override Price column is editable (cell mode, number input).
 *     Empty / null means "inherit base price".
 *   - Delete column marks a row for removal — stays visible (dimmed) until
 *     Save commits.
 *   - Ghost row at the bottom acts as the inline "+ Add" affordance. The
 *     first column on that row opens a CellAutocomplete picker; on select
 *     the row materializes into local pending state.
 *   - Save batches all changes into the existing action[] payload and
 *     POSTs (product-mode) or PATCHes (customer-mode); Cancel discards.
 */
function CustomerCatalogMatrix({ customerId, product }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const isCustomerMode = customerId != null;
  const isProductMode = product != null;

  const [catalog, setCatalog] = useState([]);
  const [edits, setEdits] = useState({}); // { [rowId]: { price } }
  const [removed, setRemoved] = useState({}); // { [rowId]: true }
  const [newRows, setNewRows] = useState([]); // { tempId, customer?, product?, price }
  const [savePending, setSavePending] = useState(false);

  const products = useSelector(state => productSlice.selectors.selectAllEntities(state));

  useEffect(() => {
    if (isCustomerMode) {
      fetchCustomerCatalog(customerId).then(setCatalog);
    } else if (isProductMode) {
      fetchCustomerCatalogsByProduct(product.id).then(setCatalog);
    }
  }, [customerId, product?.id, isCustomerMode, isProductMode, product]);

  const resetDirty = useCallback(() => {
    setEdits({});
    setRemoved({});
    setNewRows([]);
  }, []);

  const dirtyCount = Object.keys(edits).length + Object.keys(removed).filter(k => removed[k]).length + newRows.length;

  const rows = useMemo(() => {
    const existing = catalog.map(item => {
      const rowId = item.id ?? `${item.customer?.id ?? 'all'}-${item.product.id}`;
      const edited = edits[rowId];
      return {
        id: rowId,
        customer: item.customer,
        product: item.product,
        overridePrice: edited?.price !== undefined ? edited.price : (item.price ?? ''),
        inheritedPrice: item.product.item_price,
        isAllCustomers: item.id == null,
        removed: Boolean(removed[rowId]),
        _origin: 'existing',
      };
    });
    const pending = newRows.map(row => ({
      id: row.tempId,
      customer: row.customer ?? null,
      product: row.product ?? null,
      overridePrice: row.price ?? '',
      inheritedPrice: row.product?.item_price ?? null,
      isAllCustomers: false,
      removed: false,
      _origin: 'new',
    }));
    return [...existing, ...pending];
  }, [catalog, edits, removed, newRows]);

  const gridRows = useMemo(() => [...rows, { id: GHOST_ID, _ghost: true }], [rows]);

  const usedCustomerIds = useMemo(
    () => new Set([...catalog.map(c => c.customer?.id), ...newRows.map(r => r.customer?.id)].filter(id => id != null)),
    [catalog, newRows],
  );

  const usedProductIds = useMemo(
    () => new Set([...catalog.map(c => c.product?.id), ...newRows.map(r => r.product?.id)].filter(id => id != null)),
    [catalog, newRows],
  );

  const handleProcessRowUpdate = useCallback(newRow => {
    if (newRow._ghost) return newRow;
    const raw = newRow.overridePrice;
    const price = raw === '' || raw == null ? '' : raw;
    if (newRow._origin === 'new') {
      setNewRows(prev => prev.map(r => (r.tempId === newRow.id ? { ...r, price } : r)));
    } else {
      setEdits(prev => ({ ...prev, [newRow.id]: { price } }));
    }
    return newRow;
  }, []);

  const toggleRemove = useCallback(rowId => {
    setRemoved(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  const removeNew = useCallback(tempId => {
    setNewRows(prev => prev.filter(r => r.tempId !== tempId));
  }, []);

  const beginAdd = useCallback(() => {
    apiRef.current?.startCellEditMode?.({ id: GHOST_ID, field: 'addPicker' });
  }, [apiRef]);

  const commitAddCustomer = useCallback(
    opt => {
      if (!opt?.id) return;
      setNewRows(prev => [
        ...prev,
        {
          tempId: `${NEW_ROW_PREFIX}${Date.now()}-${prev.length}`,
          customer: { id: opt.id, name: opt.name ?? t('warehouse.product.customerNumber', { id: opt.id }) },
          product,
          price: '',
        },
      ]);
    },
    [product, t],
  );

  const commitAddProduct = useCallback(
    opt => {
      if (!opt?.id) return;
      setNewRows(prev => [
        ...prev,
        {
          tempId: `${NEW_ROW_PREFIX}${Date.now()}-${prev.length}`,
          customer: { id: customerId, name: null },
          product: opt,
          price: '',
        },
      ]);
    },
    [customerId],
  );

  const handleSave = useCallback(async () => {
    setSavePending(true);
    try {
      const actions = [];
      catalog.forEach(item => {
        const rowId = item.id ?? `${item.customer?.id ?? 'all'}-${item.product.id}`;
        const sku = item.product.item_part_num;
        const custId = item.customer?.id;
        if (removed[rowId]) {
          actions.push({ customer_id: custId, item_part_num: sku, action: 'remove' });
          return;
        }
        const edit = edits[rowId];
        if (edit) {
          actions.push({
            customer_id: custId,
            item_part_num: sku,
            action: 'upsert',
            price: edit.price === '' ? null : parseFloat(edit.price),
          });
        }
      });
      newRows.forEach(row => {
        if (!row.product?.item_part_num) return;
        actions.push({
          customer_id: row.customer?.id,
          item_part_num: row.product.item_part_num,
          action: 'upsert',
          price: row.price === '' || row.price == null ? null : parseFloat(row.price),
        });
      });

      let updated;
      if (isCustomerMode) {
        updated = await patchCustomerCatalog(customerId, actions);
      } else {
        updated = await postCustomerCatalog(actions);
      }
      setCatalog(updated);
      resetDirty();
    } finally {
      setSavePending(false);
    }
  }, [catalog, edits, removed, newRows, isCustomerMode, customerId, resetDirty]);

  // Async loader for customers (product-mode add picker). The customer slice
  // isn't preloaded on the product form, so we hit the API directly with the
  // user's search term.
  const loadCustomerOptions = useCallback(
    async search => {
      const res = await dispatch(fetchCustomerListWithFilters(0, 25, {}, search?.trim() ?? '', ['name']));
      const data = res?.toJS?.() ?? res;
      const list = Array.isArray(data) ? data : (data?.results ?? []);
      return list.map(c => ({ id: c.id, name: c.name }));
    },
    [dispatch],
  );

  const ghostAddCell = useMemo(
    () => (
      <Box
        onClick={beginAdd}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          height: '100%',
          color: 'text.secondary',
          cursor: 'pointer',
          '&:hover': { color: 'primary.main' },
        }}
      >
        <Add fontSize="small" />
        <Typography variant="body2">
          {isProductMode ? t('warehouse.product.addCustomer') : t('warehouse.catalogMatrix.addProduct')}
        </Typography>
      </Box>
    ),
    [beginAdd, isProductMode, t],
  );

  const columns = useMemo(() => {
    const cols = [];

    if (isProductMode) {
      cols.push({
        field: 'addPicker',
        headerName: t('general.customer'),
        flex: 1,
        minWidth: 220,
        display: 'flex',
        sortable: false,
        editable: true,
        renderCell: params => {
          if (params.row._ghost) return ghostAddCell;
          if (params.row.isAllCustomers) {
            return <Chip size="small" label={t('warehouse.product.allCustomers')} color="info" variant="outlined" />;
          }
          return <Typography variant="body2">{params.row.customer?.name ?? '—'}</Typography>;
        },
        renderEditCell: editParams => {
          const stop = () => apiRef.current?.stopCellEditMode?.({ id: editParams.id, field: editParams.field });
          return (
            <CellAutocomplete
              loadOptions={loadCustomerOptions}
              getOptionLabel={opt => opt.name}
              getOptionKey={opt => opt?.id}
              disableOption={opt => usedCustomerIds.has(opt.id)}
              onSelect={picked => {
                if (picked) commitAddCustomer(picked);
                stop();
              }}
              onCancel={stop}
              placeholder={t('warehouse.catalogMatrix.searchCustomers')}
            />
          );
        },
      });
    } else if (isCustomerMode) {
      cols.push({
        field: 'addPicker',
        headerName: t('warehouse.catalogMatrix.productColumn'),
        flex: 1,
        minWidth: 280,
        display: 'flex',
        sortable: false,
        editable: true,
        renderCell: params => {
          if (params.row._ghost) return ghostAddCell;
          const p = params.row.product;
          if (!p) return null;
          return (
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2">
                <Link
                  component={RouterLink}
                  to={`/sales/products/${p.id}`}
                  underline="hover"
                  sx={{ fontFamily: 'monospace', color: 'text.secondary', mr: 0.75 }}
                  onClick={e => e.stopPropagation()}
                >
                  {p.item_part_num}:
                </Link>
                {p.item_name}
              </Typography>
              {p.item_description && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {p.item_description}
                </Typography>
              )}
            </Box>
          );
        },
        renderEditCell: editParams => {
          const stop = () => apiRef.current?.stopCellEditMode?.({ id: editParams.id, field: editParams.field });
          return (
            <CellAutocomplete
              options={products}
              getOptionLabel={opt => `${opt.item_part_num}: ${opt.item_name}`}
              renderOptionRow={opt => (
                <>
                  <Typography variant="body2">
                    <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', mr: 0.75 }}>
                      {opt.item_part_num}:
                    </Box>
                    {opt.item_name}
                  </Typography>
                  {opt.item_description && (
                    <Typography variant="caption" color="text.secondary">
                      {opt.item_description}
                    </Typography>
                  )}
                </>
              )}
              disableOption={opt => usedProductIds.has(opt.id)}
              onSelect={picked => {
                if (picked) commitAddProduct(picked);
                stop();
              }}
              onCancel={stop}
              placeholder={t('warehouse.componentMatrix.searchProducts')}
            />
          );
        },
      });
    }

    cols.push({
      field: 'overridePrice',
      headerName: t('warehouse.catalogMatrix.overridePrice'),
      width: 160,
      editable: true,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: params => {
        if (params.row._ghost) return null;
        if (params.row.removed) {
          return (
            <Typography variant="body2" sx={{ color: 'text.disabled', textDecoration: 'line-through' }}>
              {params.value === '' || params.value == null ? '—' : `$${params.value}`}
            </Typography>
          );
        }
        if (params.value === '' || params.value == null) {
          return (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              {t('warehouse.catalogMatrix.inherits')}
            </Typography>
          );
        }
        const isEdited = Boolean(edits[params.id]);
        return (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: isEdited ? 600 : 400 }}>
            ${params.value}
          </Typography>
        );
      },
    });

    cols.push({
      field: 'inheritedPrice',
      headerName: t('warehouse.catalogMatrix.basePrice'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      sortable: false,
      renderCell: params => {
        if (params.row._ghost) return null;
        return params.value != null ? (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            ${params.value}
          </Typography>
        ) : null;
      },
    });

    cols.push({
      field: 'actions',
      headerName: '',
      width: 64,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
      renderCell: params => {
        if (params.row._ghost) return null;
        if (params.row._origin === 'new') {
          return (
            <IconButton
              size="small"
              onClick={() => removeNew(params.id)}
              aria-label={t('warehouse.catalogMatrix.removePendingRow')}
            >
              <Delete fontSize="small" />
            </IconButton>
          );
        }
        if (params.row.isAllCustomers) return null;
        return (
          <IconButton
            size="small"
            onClick={() => toggleRemove(params.id)}
            aria-label={params.row.removed ? t('warehouse.catalogMatrix.undoRemove') : t('actions.remove', 'Remove')}
            color={params.row.removed ? 'error' : 'default'}
          >
            <Delete fontSize="small" />
          </IconButton>
        );
      },
    });

    return cols;
  }, [
    isProductMode,
    isCustomerMode,
    edits,
    apiRef,
    products,
    usedCustomerIds,
    usedProductIds,
    ghostAddCell,
    loadCustomerOptions,
    commitAddCustomer,
    commitAddProduct,
    toggleRemove,
    removeNew,
    t,
  ]);

  const isCellEditable = useCallback(params => {
    if (params.row._ghost) return params.field === 'addPicker';
    return params.field === 'overridePrice' && !params.row.removed;
  }, []);

  const getRowClassName = useCallback(
    params => (params.row._ghost ? 'ghost-row' : params.row.removed ? 'row-removed' : ''),
    [],
  );

  return (
    <Box>
      {dirtyCount > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button size="small" onClick={resetDirty} disabled={savePending}>
            {t('actions.cancel')}
          </Button>
          <Button size="small" variant="contained" onClick={handleSave} disabled={savePending}>
            {t('warehouse.catalogMatrix.saveChangesCount', { count: dirtyCount })}
          </Button>
        </Stack>
      )}

      <DataGridPro
        apiRef={apiRef}
        rows={gridRows}
        columns={columns}
        autoHeight
        hideFooter
        disableRowSelectionOnClick
        disableColumnFilter
        processRowUpdate={handleProcessRowUpdate}
        isCellEditable={isCellEditable}
        getRowClassName={getRowClassName}
        sx={{
          '& .row-removed .MuiDataGrid-cell': { opacity: 0.5 },
          '& .ghost-row .MuiDataGrid-cell': { borderBottom: 'none' },
        }}
        localeText={{ noRowsLabel: t('warehouse.catalogMatrix.noCustomerPricingYet') }}
      />
    </Box>
  );
}

export default CustomerCatalogMatrix;
